import React, { useEffect, useMemo, useRef } from 'react'
import { Box, Flex, FormControl } from '@chakra-ui/core'
import { Controller, useFormContext } from 'react-hook-form'
import useUploadService from 'hooks/useUploadService'
import { useFileValidation } from 'hooks/useFileValidation'
import MediaGallery from 'components/ui/MediaGallery'
import HiddenFileInput from 'components/ui/Input/HiddenFileInput'
import { WidgetWrapper } from 'components/TasksV2/UI/WidgetWrapper'
import useLocaleText from 'components/useLocaleText'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import WidgetHeader from 'components/TasksV2/UI/WidgetHeader'
import { Content } from 'components/TasksV2/TaskCreation/FormStepContext'
import { AddMediaButtonts } from '../../Description/AddMediaButtonts'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'
import strings from 'constants/strings'
import {
  EditorContent,
  RichTextEditor,
  getRichTextByteSize,
  MAX_RICH_TEXT_BYTES,
} from 'components/CommonComponents/RichTextEditor'
import { ErrorMessage } from 'components/ui/Input/ErrorMessage'
import { ImageEditorModal } from 'components/CommonComponents/ImageEditor/ImageEditorModal'
import { useImageDraftEditor } from 'hooks/useImageDraftEditor'
import { useBuilderWidget } from '../../../../contexts/BuilderWidgetContext'
import { MediaUploadActions, mediaUploadLiterals } from '../../../../components/MediaUploadActions'
import { useMediaFileUploads } from '../../../../hooks/useMediaFileUploads'
import { ReferenceMediaSection } from '../../../../components/ReferenceMediaSection'
import { hasOnlyUploadedMedia } from '../../../../model/mediaUploads'

interface DescriptionWidgetProps {
  index: number
  isDragging: boolean
  field: Content
  subtaskPath: string
  remove: () => void
}

const DescriptionWidget: React.FC<DescriptionWidgetProps> = ({ index, isDragging, field, subtaskPath, remove }) => {
  const builder = useBuilderWidget()
  const uploadText = useLocaleText(mediaUploadLiterals)
  const { getValues, setValue, control, watch } = useFormContext()
  const { setIsMediaUploading, isEdit } = useFormStepContext()
  const { uploadFile: uploadToS3 } = useUploadService()
  const { fileSizeValidation } = useFileValidation()
  const t_say_something = useLocaleText('t_say_something')
  const t_text_too_long = useLocaleText('text_too_long')

  const imageInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const formImages: string[] = watch(`${subtaskPath}.images`) || []
  const formVideos: string[] = watch(`${subtaskPath}.videos`) || []
  const formFiles: string[] = watch(`${subtaskPath}.files`) || []

  const richTextValue = watch(`${subtaskPath}.rich_text`) as EditorContent | null

  const richTextError = useMemo(() => {
    const bytes = getRichTextByteSize(richTextValue)

    return bytes > MAX_RICH_TEXT_BYTES ? t_text_too_long || 'Text is too long (max 4KB)' : undefined
  }, [richTextValue, t_text_too_long])

  const {
    isUploading,
    editorOpen,
    editorStartIndex,
    uploadingUrls: imageUploadingUrls,
    failedUrls: imageFailedUrls,
    uploadLocalBlobs,
    hasPendingImages,
    setEditorStartIndex,
    setEditorOpen,
    createDraftBlobs,
    handleEditorSave,
    handleEditorCancel,
    removeDraftImage,
  } = useImageDraftEditor({
    images: formImages,
    onChange: (imgs) => setValue(`${subtaskPath}.images`, imgs),
    uploadFile: uploadToS3,
  })

  const videos = useMediaFileUploads({
    getUrls: () => getValues(`${subtaskPath}.videos`) || [],
    onChange: (urls) => setValue(`${subtaskPath}.videos`, urls),
    uploadFile: uploadToS3,
  })
  const files = useMediaFileUploads({
    getUrls: () => getValues(`${subtaskPath}.files`) || [],
    onChange: (urls) => setValue(`${subtaskPath}.files`, urls),
    uploadFile: uploadToS3,
  })
  const busy = isUploading || videos.isUploading || files.isUploading || editorOpen
  const uploadingUrls = new Set([
    ...Array.from(imageUploadingUrls),
    ...Array.from(videos.uploadingUrls),
    ...Array.from(files.uploadingUrls),
  ])
  const failedUrls = new Set([
    ...Array.from(imageFailedUrls),
    ...Array.from(videos.failedUrls),
    ...Array.from(files.failedUrls),
  ])

  const addMedia = (selected: File[]) => {
    const allFiles = selected.filter(fileSizeValidation)

    if (!allFiles.length) return

    const imageFiles = allFiles.filter((f) => f.type.startsWith('image'))
    const videoFiles = allFiles.filter((f) => f.type.startsWith('video'))

    if (imageFiles.length) {
      const { firstNewIndex } = createDraftBlobs(imageFiles)
      setEditorStartIndex(firstNewIndex)
      setEditorOpen(true)
      setIsMediaUploading(true)
    }

    if (videoFiles.length) {
      setIsMediaUploading(true)
      videos.add(videoFiles)
    }
  }

  const addFiles = (selected: File[]) => {
    const validFiles = selected.filter(fileSizeValidation)
    if (validFiles.length) {
      setIsMediaUploading(true)
      files.add(validFiles)
    }
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addMedia(Array.from(event.target.files || []))
    event.target.value = ''
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files || []))
    event.target.value = ''
  }

  const handleImageClick = (imageIndex: number) => {
    setEditorStartIndex(imageIndex)
    setEditorOpen(true)
  }

  const handleRemoveMedia = (index: number) => {
    if (index < formImages.length) {
      removeDraftImage(formImages[index])
      setValue(
        `${subtaskPath}.images`,
        formImages.filter((_: string, i: number) => i !== index)
      )
    } else {
      videos.remove(formVideos[index - formImages.length])
    }
  }

  const handleRemoveFile = (url: string) => files.remove(url)

  const retryMedia = (index: number) => {
    if (index < formImages.length) void uploadLocalBlobs([formImages[index]])
    else videos.retry(formVideos[index - formImages.length])
  }

  const handleUploadMedia = async (selected: File[]) => {
    addMedia(selected.filter((file) => file.type.startsWith('image') || file.type.startsWith('video')))
    addFiles(selected.filter((file) => !file.type.startsWith('image') && !file.type.startsWith('video')))
  }

  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleUploadMedia,
    accept: `${strings.POST_FILE_ACCEPT_EXTENSIONS},image/*,video/*`,
  })

  useEffect(() => {
    setIsMediaUploading(busy)
  }, [busy, setIsMediaUploading])

  useEffect(() => {
    setValue(`${subtaskPath}.index_ref`, index)
  }, [index, setValue, subtaskPath])

  return (
    <>
      <DropzoneWrapper onDrop={onDrop} onPaste={handlePaste}>
        <WidgetWrapper pos="relative" isError={!!richTextError} isDragging={isDragging}>
          {!isEdit && <WidgetHeader onRemove={remove} subtaskType={field.type} />}

          <FormControl w="100%">
            <Controller
              name={`${subtaskPath}.images`}
              control={control}
              defaultValue={field.images || []}
              rules={{ validate: (urls) => hasOnlyUploadedMedia(urls) || uploadText.failed }}
              as={<Box display="none" />}
            />
            <Controller
              name={`${subtaskPath}.videos`}
              control={control}
              defaultValue={field.videos || []}
              rules={{ validate: (urls) => hasOnlyUploadedMedia(urls) || uploadText.failed }}
              as={<Box display="none" />}
            />
            <Controller
              name={`${subtaskPath}.files`}
              control={control}
              defaultValue={field.files || []}
              rules={{ validate: (urls) => hasOnlyUploadedMedia(urls) || uploadText.failed }}
              as={<Box display="none" />}
            />
            <Controller
              name={`${subtaskPath}.type`}
              control={control}
              defaultValue={field.type}
              as={<Box display="none" />}
            />
            <Controller
              name={`${subtaskPath}.index_ref`}
              control={control}
              defaultValue={index}
              as={<Box display="none" />}
            />

            <Controller
              name={`${subtaskPath}.rich_text`}
              control={control}
              as={RichTextEditor}
              toolbarVisibility="always"
              defaultValue={(getValues(`${subtaskPath}.rich_text`) as EditorContent) || null}
              placeholder={t_say_something}
              contentMinHeight={builder ? '120px' : undefined}
              containerStyle={{
                border: builder ? '1px solid #e2e5e8' : 'none',
                ...(builder ? { minHeight: '100px' } : {}),
                borderRadius: '12px',
              }}
              rules={{
                validate: (value: EditorContent) => {
                  const bytes = getRichTextByteSize(value)

                  return bytes <= MAX_RICH_TEXT_BYTES || t_text_too_long || 'Text is too long (max 4KB)'
                },
              }}
            />
            <ReferenceMediaSection enabled={builder}>
              {builder ? (
                <MediaUploadActions
                  disabled={busy}
                  onImage={() => imageInputRef.current?.click()}
                  onVideo={() => videoInputRef.current?.click()}
                  onFile={() => fileInputRef.current?.click()}
                  onCapture={(file) => addMedia([file])}
                />
              ) : (
                <Flex p="10px" justifyContent="flex-end">
                  <AddMediaButtonts
                    onFileUploadClick={() => fileInputRef.current?.click()}
                    onImageUploadClick={() => imageInputRef.current?.click()}
                  />
                </Flex>
              )}
              <MediaGallery
                fileAppearance="type"
                attachedImages={formImages}
                attachedFiles={formFiles}
                attachedVideos={formVideos}
                onRemoveMedia={handleRemoveMedia}
                onRemoveFile={handleRemoveFile}
                uploadingUrls={uploadingUrls}
                failedUrls={failedUrls}
                onRetryMedia={retryMedia}
                onRetryFile={files.retry}
                onImageClick={handleImageClick}
              />

              {failedUrls.size > 0 && <p role="alert">{uploadText.failed}</p>}
            </ReferenceMediaSection>
            <HiddenFileInput accept="video/*" multiple onChange={handleImageUpload} ref={videoInputRef} />
            <HiddenFileInput
              accept={builder ? 'image/*' : 'image/*, video/*'}
              multiple
              onChange={handleImageUpload}
              ref={imageInputRef}
            />
            <HiddenFileInput accept="*/*" multiple onChange={handleFileUpload} ref={fileInputRef} />
          </FormControl>
        </WidgetWrapper>
      </DropzoneWrapper>

      {richTextError && (
        <Box px="10px">
          <ErrorMessage errorMessage={richTextError} />
        </Box>
      )}

      <ImageEditorModal
        images={formImages}
        startIndex={editorStartIndex}
        isOpen={editorOpen}
        onSave={handleEditorSave}
        onCancel={handleEditorCancel}
        hasLocalImages={hasPendingImages}
      />
    </>
  )
}

export default DescriptionWidget
